import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@components/primitives/Button';
import { useSubscriptionDetails } from '@features/user';
import { Box, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { unixTimestampToDaysRemaining } from '@utils/utils';

export const TrialBanner = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { navigation: navigationMetrics } = useEventTracking();

  // Fetch subscription details from the new API
  const { data: subscriptionDetails } = useSubscriptionDetails();

  // Calculate trial status
  const trialInfo = useMemo(() => {
    if (subscriptionDetails?.status !== 'trialing' || !subscriptionDetails?.current_period_end) {
      return null;
    }

    const remainingSeconds = subscriptionDetails.current_period_end - Math.floor(Date.now() / 1000);

    if (remainingSeconds <= 86400) {
      // Less than 24 hours
      return {
        boldText: 'Your free trial ends today!',
        regularText: '',
      };
    }

    const daysRemaining = unixTimestampToDaysRemaining(subscriptionDetails.current_period_end);

    return {
      boldText: `${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'}`,
      regularText: 'left in your free trial',
    };
  }, [subscriptionDetails?.status, subscriptionDetails?.current_period_end]);

  const handleSubscribeClick = () => {
    // Track billing quick action
    navigationMetrics.quickAction({
      action_type: 'billing_portal',
      source_path: location.pathname,
    });

    navigate('/practice/subscriptions');
  };

  // Don't show banner if not in trial
  if (!trialInfo) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 500,
            fontSize: { xs: '0.875rem', sm: '0.9375rem' },
          }}
        >
          <strong>{trialInfo.boldText}</strong>
          {trialInfo.regularText && ` ${trialInfo.regularText}`}
        </Typography>
      </Box>

      <Button variant="contained" color="secondary" onClick={handleSubscribeClick}>
        Subscribe
      </Button>
    </Box>
  );
};
